import * as React from 'react';
import { FeatureCollection } from "geojson"; 
import * as turf from '@turf/helpers';
import { LngLatBounds } from 'mapbox-gl';
import Map, { MapboxMap, Source, Layer, ViewStateChangeEvent, MapLayerMouseEvent, Popup } from 'react-map-gl';

import { App } from '../../App';
import { MarkersCircleLayer, MarkersStrokeLayer, MarkersStrokeHoverLayer, ClustersCircleLayer, ClustersStrokeLayer, ClustersStrokeHoverLayer, ClustersSymbolLayer } from '../Layers';
import { IMapElement } from './IMapElement';
import { MapControls } from '../MapControls/MapControls';

interface IProps {
  /**
   * List of marker locations to display:
   */
  elements: IMapElement[];
  /**
   * Callback for when map is moved/zoomed:
   */
  onChange: (bounds: LngLatBounds, zoom: number) => void; 
  /**
   * When this random number changes, the map
   * will execute onChange.
   */
  time: number;  
  /**
   * Callback that produces popup content for a marker.
   */
  popup: (item: any) => React.ReactNode;
  /**
   * Callback is fired when map is loaded.
   */
  onLoad?: (map: MapboxMap) => void;
}

interface IHover {
  item: IMapElement;
  latitude: number;
  longitude: number;
}

const ClusteredMap = (props: IProps) => {
  const map = React.useRef(null as MapboxMap);
  const [viewState, setViewState] = React.useState({
    longitude: 0,
    latitude: 0, 
    zoom: 1,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 }    
  });
  const [interactiveLayerIds, setInteractiveLayerIds] = React.useState([]);
  const [hover, setHover] = React.useState({ item: null } as IHover);
  const [selected, setSelected] = React.useState(null as IMapElement);

  const requestData = () => {
    if(map.current != null) props.onChange(map.current.getBounds(), map.current.getZoom());
  }

  React.useEffect(() => {
    requestData();
  }, [props.time]);

  const handleLoad = (e: mapboxgl.MapboxEvent) => {
    map.current = e.target;
    setInteractiveLayerIds([ 'clusters-circles', 'markers-circles' ]);
    requestData();
    if(props.onLoad) props.onLoad(e.target);
  }

  const handleMove = (e: ViewStateChangeEvent) => {
    setViewState(e.viewState);
  }

  const handleMoveEnd = () => {
    requestData();
  }

  const handleMouseMove = (e: MapLayerMouseEvent) => {
    // If source isn't yet loaded, MapGL throws an error:
    if(!map.current || !map.current.loaded) return;

    // Turn hovering off:
    setHover({ ...hover, item: null });
    if(e.features.length == 0) return;

    // If hovering over a feature, set hover to feature's id property.
    const featureID = e.features[0].properties.id;
    
    setHover({
      item: props.elements.find(e => e.id === featureID),
      latitude: e.lngLat.lat,
      longitude: e.lngLat.lng
    });
  }  

  const handleMouseLeave = (e: MapLayerMouseEvent) => {
    setHover({ ...hover, item: null });
  }

  const handleClick = (e: MapLayerMouseEvent) => {
    // If source isn't yet loaded, MapGL throws an error:
    if(!map.current || !map.current.loaded) return;

    setSelected(null);
    if(e.features.length == 0) return;

    const featureID = e.features[0].properties.id;
    if(e.features[0].properties.count) return; // Ignore clusters.

    const element = props.elements.find(e => e.id === featureID);
    setSelected({...element});
  }    

  const clustersGeoJSON = (): FeatureCollection => {
    const featureCollection = turf.featureCollection(
      props.elements
        .filter(elem => elem.count)
        .map(elem => { 
        const p = turf.point([elem.longitude, elem.latitude]);
        p.properties.id = elem.id;
        p.properties.count = elem.count;
        return p;
      })
    );
    return featureCollection;
  }

  const markersGeoJSON = (): FeatureCollection => {
    const featureCollection = turf.featureCollection(
      props.elements
        .filter(elem => !elem.count)
        .map(elem => { 
        const p = turf.point([elem.longitude, elem.latitude]);
        p.properties.id = elem.id;
        return p;
      })
    );
    return featureCollection;    
  }

  return (
    <Map
      {...viewState}
      mapboxAccessToken={App.config.mapboxKey}
      style={{width: '100%', height: '100%'}}
      cursor={hover.item ? 'pointer' : 'auto'}
      logoPosition="bottom-left"  // Moves mapbox logo
      fadeDuration={0}            // Prevents fading in of cluster labels
      interactiveLayerIds={interactiveLayerIds}
      mapStyle="mapbox://styles/longlineenvironment/cl8x4af5v003n14ld74f6ugwd"
      minZoom={1}
      maxZoom={20}
      doubleClickZoom={false}
      //Events
      onLoad={handleLoad}
      onMove={handleMove}      
      onMoveEnd={handleMoveEnd}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <MapControls {...viewState}/>
      
      <Source type="geojson" data={clustersGeoJSON()}>
        <Layer {...ClustersCircleLayer}/>
        <Layer {...ClustersStrokeLayer} filter={['!=', ['get', 'id'], hover.item ? hover.item.id : null ]}/>
        <Layer {...ClustersStrokeHoverLayer} filter={['==', ['get', 'id'], hover.item ? hover.item.id : null ]}/>
        <Layer {...ClustersSymbolLayer}/>
      </Source>
      <Source type="geojson" data={markersGeoJSON()}>
        <Layer {...MarkersCircleLayer}/>
        <Layer {...MarkersStrokeLayer} filter={['!=', ['get', 'id'], hover.item ? hover.item.id : null ]}/>
        <Layer {...MarkersStrokeHoverLayer} filter={['==', ['get', 'id'], hover.item ? hover.item.id : null ]}/>
      </Source>

      {selected && (<Popup
          latitude={selected.latitude}
          longitude={selected.longitude}
          anchor="bottom"
          closeButton={false}
          closeOnClick={false} 
          offset={[0,-15]}
        >
          {props.popup(selected)}
        </Popup>)}

    </Map>
  );
}

export { ClusteredMap }
